/**
 * MODULE: _shared/frontend/lib/frontend/utils/formatCurrency.ts
 * PURPOSE: Single locale-aware currency formatter for shell and CRM (grouping, symbol, fraction rules)
 * DEPENDENCIES: None
 * PUBLIC_API: formatCurrency, FormatCurrencyOptions, DEFAULT_CRM_CURRENCY, DEFAULT_CRM_LOCALE
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: ~55/150
 */

export const DEFAULT_CRM_CURRENCY = 'GBP';
export const DEFAULT_CRM_LOCALE = 'en-GB';

export type FormatCurrencyOptions = {
  currency?: string;
  locale?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

/**
 * Format a numeric amount with Intl (e.g. £36,470 for GBP en-GB with zero fraction digits).
 */
export function formatCurrency(amount: number, options?: FormatCurrencyOptions): string {
  const currency = options?.currency ?? DEFAULT_CRM_CURRENCY;
  const locale = options?.locale ?? localeForCurrency(currency);
  const minimumFractionDigits = options?.minimumFractionDigits ?? 0;
  const maximumFractionDigits = options?.maximumFractionDigits ?? 0;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount);
}

/** Map ISO 4217 code to a sensible Intl locale for symbol placement. */
export function localeForCurrency(currency: string): string {
  switch (currency.toUpperCase()) {
    case 'USD':
      return 'en-US';
    case 'EUR':
      return 'de-DE';
    case 'GBP':
    default:
      return DEFAULT_CRM_LOCALE;
  }
}

/** Currency symbol for labels (e.g. £, $, €) from ISO 4217 code. */
export function currencySymbolForCode(
  currency: string = DEFAULT_CRM_CURRENCY,
  locale?: string
): string {
  const resolvedLocale = locale ?? localeForCurrency(currency);
  return (
    new Intl.NumberFormat(resolvedLocale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
      .formatToParts(0)
      .find((p) => p.type === 'currency')?.value ?? currency.toUpperCase()
  );
}

/** Compact K/M labels using tenant currency symbol (dashboard KPI tiles). */
export function formatCompactCurrency(
  value: number,
  currency: string = DEFAULT_CRM_CURRENCY,
  locale?: string
): string {
  const resolvedLocale = locale ?? localeForCurrency(currency);
  const symbol =
    new Intl.NumberFormat(resolvedLocale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
      .formatToParts(0)
      .find((p) => p.type === 'currency')?.value ?? currency;

  if (value >= 1_000_000) return `${symbol}${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${symbol}${(value / 1_000).toFixed(0)}K`;
  return formatCurrency(value, { currency, locale: resolvedLocale });
}

/** Build deal-size bucket labels from numeric thresholds in tenant currency. */
export function formatCurrencyRangeLabel(
  min: number,
  max: number,
  currency: string = DEFAULT_CRM_CURRENCY,
  locale?: string
): string {
  const resolvedLocale = locale ?? localeForCurrency(currency);
  const fmt = (n: number) =>
    formatCurrency(n, { currency, locale: resolvedLocale, maximumFractionDigits: 0 });
  if (max === Infinity) return `${fmt(min)}+`;
  if (min <= 0) return `<${fmt(max)}`;
  return `${fmt(min)}–${fmt(max)}`;
}
